import { useEffect, useState } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { supabase } from "@/lib/supabase"
import { useAuthStore } from "@/stores/authStore"

type InviteRole = "owner" | "admin" | "editor" | "viewer"

interface InviteData {
  id: string
  org_id: string
  email: string
  role: InviteRole
  expires_at: string
  accepted_at: string | null
  organization: {
    id: string
    name: string
    slug: string
  }
}

function getRoleBadgeClass(role: InviteRole): string {
  switch (role) {
    case "owner":
      return "bg-blue-100 text-blue-800 border-blue-200"
    case "admin":
      return "bg-purple-100 text-purple-800 border-purple-200"
    case "editor":
      return "bg-green-100 text-green-800 border-green-200"
    case "viewer":
      return "bg-gray-100 text-gray-800 border-gray-200"
    default:
      return "bg-gray-100 text-gray-800 border-gray-200"
  }
}

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, isLoading: authLoading } = useAuthStore()
  const token = searchParams.get("token")

  const [invite, setInvite] = useState<InviteData | null>(null)
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [alreadyMember, setAlreadyMember] = useState(false)

  useEffect(() => {
    if (!token) {
      setError("Invalid invite link")
      setLoading(false)
      return
    }

    loadInvite()
  }, [token])

  useEffect(() => {
    // Check if user is already a member when they log in
    if (user && invite && !alreadyMember) {
      checkIfAlreadyMember()
    }
  }, [user, invite])

  const loadInvite = async () => {
    if (!token) return

    setLoading(true)

    const { data, error: fetchError } = await supabase
      .from("invites")
      .select(`
        id,
        org_id,
        email,
        role,
        expires_at,
        accepted_at,
        organization:org_id (
          id,
          name,
          slug
        )
      `)
      .eq("token", token)
      .single()

    if (fetchError || !data) {
      setError("Invite not found")
      setLoading(false)
      return
    }

    // Check if already accepted
    if (data.accepted_at) {
      setError("This invite has already been used")
      setLoading(false)
      return
    }

    // Check if expired
    if (new Date(data.expires_at) < new Date()) {
      setError("This invite has expired")
      setLoading(false)
      return
    }

    // Handle the organization data properly
    const orgData = data.organization as unknown as { id: string; name: string; slug: string } | null
    if (!orgData) {
      setError("Organization not found")
      setLoading(false)
      return
    }

    setInvite({
      id: data.id,
      org_id: data.org_id,
      email: data.email,
      role: data.role as InviteRole,
      expires_at: data.expires_at,
      accepted_at: data.accepted_at,
      organization: orgData,
    })
    setLoading(false)
  }

  const checkIfAlreadyMember = async () => {
    if (!user || !invite) return

    const { data: existingMember } = await supabase
      .from("organization_members")
      .select("id")
      .eq("org_id", invite.org_id)
      .eq("user_id", user.id)
      .maybeSingle()

    if (existingMember) {
      setAlreadyMember(true)
    }
  }

  const handleAccept = async () => {
    if (!user || !invite) return

    setAccepting(true)

    // Check if already a member
    const { data: existingMember } = await supabase
      .from("organization_members")
      .select("id")
      .eq("org_id", invite.org_id)
      .eq("user_id", user.id)
      .maybeSingle()

    if (existingMember) {
      setAlreadyMember(true)
      setAccepting(false)
      return
    }

    // Add user to organization_members
    const { error: memberError } = await supabase
      .from("organization_members")
      .insert({
        org_id: invite.org_id,
        user_id: user.id,
        role: invite.role,
      })

    if (memberError) {
      toast.error("Failed to join organization")
      console.error(memberError)
      setAccepting(false)
      return
    }

    // Mark invite as accepted
    await supabase
      .from("invites")
      .update({ accepted_at: new Date().toISOString() })
      .eq("id", invite.id)

    toast.success(`Welcome to ${invite.organization.name}!`)

    // Redirect to dashboard
    navigate("/dashboard")
  }

  const handleSignUp = () => {
    navigate(`/signup?redirect=/accept-invite?token=${token}`)
  }

  const handleLogin = () => {
    navigate(`/login?redirect=/accept-invite?token=${token}`)
  }

  const handleGoToDashboard = () => {
    navigate("/dashboard")
  }

  // Show loading while auth is initializing
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-8">
            <p className="text-center text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-destructive">Invalid Invite</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate("/")} variant="outline">
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Show already a member state
  if (alreadyMember && invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Already a Member</CardTitle>
            <CardDescription>
              You're already a member of {invite.organization.name}.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={handleGoToDashboard}>Go to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Show invite details
  if (!invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-8">
            <p className="text-center text-muted-foreground">Invite not found</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">You've been invited!</CardTitle>
          <CardDescription>
            You've been invited to join an organization on NOS.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
            <div>
              <p className="text-sm text-muted-foreground">Organization</p>
              <p className="font-semibold text-lg">{invite.organization.name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Your Role</p>
              <Badge className={getRoleBadgeClass(invite.role)}>
                {invite.role.charAt(0).toUpperCase() + invite.role.slice(1)}
              </Badge>
            </div>
          </div>

          {!user ? (
            <div className="space-y-4">
              <p className="text-sm text-center text-muted-foreground">
                Sign up or log in to accept this invitation.
              </p>
              <div className="flex flex-col gap-3">
                <Button onClick={handleSignUp} className="w-full">
                  Create Account
                </Button>
                <Button onClick={handleLogin} variant="outline" className="w-full">
                  Log In
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-center text-muted-foreground">
                Logged in as <strong>{user.email}</strong>
              </p>
              <div className="flex flex-col gap-3">
                <Button onClick={handleAccept} disabled={accepting} className="w-full">
                  {accepting ? "Joining..." : "Accept Invite"}
                </Button>
                <Button onClick={handleGoToDashboard} variant="outline" className="w-full">
                  Decline
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
